import { loadGsap } from './gsap-loader.js';

/**
 * The "fly into the product" animation for any product card, the same one the homepage wall uses.
 *
 * A link opts in with `data-zoom-product` and `data-zoom-image` (a full-size picture of the work). Its framed picture is
 * copied, flown to the middle of the screen at a size the image can fill, and handed to the product page, which settles
 * it into place (see the handoff in sections/product-studio.liquid). Without GSAP, with reduced motion, or for a
 * new-tab click, the link just works as a normal link.
 */

/** The framed picture inside a card: an <img> that carries its own frame, or a frame element holding a mat and image. */
const SOURCE = '[data-zoom-source], .ps__related-visual img, .sp-card__frame';
const BONE = '#e5e3dd';

let layer = null;
let busy = false;

const px = (value, factor) => `${(parseFloat(value) || 0) * factor}px`;

/** Copies the framed picture as one self-contained element, with the sizes it has on screen right now. */
function buildClone(source, rect, image) {
  const isImage = source.tagName === 'IMG';
  const shown = source.offsetWidth ? rect.width / source.offsetWidth : 1;
  const frameStyle = getComputedStyle(source);
  const mat = isImage ? source : source.querySelector('[class*="mat"]') || source;
  const matStyle = getComputedStyle(mat);
  const picture = isImage ? source : source.querySelector('img');

  const edge = isImage ? frameStyle.borderTopWidth : frameStyle.paddingTop;
  const edgeColor = isImage ? frameStyle.borderTopColor : frameStyle.backgroundColor;
  const shadow =
    frameStyle.boxShadow === 'none'
      ? 'none'
      : frameStyle.boxShadow.replace(/(-?\d*\.?\d+)px/g, (_, n) => `${parseFloat(n) * shown}px`);

  const print = picture.cloneNode();
  ['loading', 'srcset', 'sizes'].forEach((name) => print.removeAttribute(name));
  print.style.cssText =
    'display:block;width:100%;height:100%;max-width:none;max-height:none;object-fit:cover;border:0;padding:0;box-shadow:none;background:none;filter:none;transform:none';
  if (image) {
    print.src = image;
    print.decoding = 'sync';
  }

  const clone = document.createElement('div');
  clone.className = 'aa-zoom__frame';
  clone.append(print);
  clone.style.cssText = [
    'position:fixed',
    'margin:0',
    'max-width:none',
    'translate:none',
    'box-sizing:border-box',
    'overflow:hidden',
    `left:${rect.left}px`,
    `top:${rect.top}px`,
    `width:${rect.width}px`,
    `height:${rect.height}px`,
    `border:${px(edge, shown)} solid ${edgeColor}`,
    `padding:${px(matStyle.paddingTop, shown)}`,
    `background:${isImage ? frameStyle.backgroundColor : matStyle.backgroundColor}`,
    `box-shadow:${shadow}`,
    'transform-origin:50% 50%',
    'pointer-events:none',
  ].join(';');
  return clone;
}

async function fly(link, source) {
  const gsap = await loadGsap();
  if (!gsap) return false;

  // Decode the full-size picture first, so nothing blanks or pops in during the flight.
  const image = link.dataset.zoomImage;
  let hiRes = false;
  if (image) {
    const preload = new Image();
    preload.src = image;
    hiRes = await Promise.race([
      preload.decode().then(
        () => true,
        () => false
      ),
      new Promise((resolve) => setTimeout(() => resolve(false), 600)),
    ]);
  }

  // Measured after the wait, so the flight is aimed at where the card is now, not where it was at the click.
  const rect = source.getBoundingClientRect();
  const clone = buildClone(source, rect, hiRes ? image : '');
  layer = document.createElement('div');
  layer.className = 'aa-zoom';
  layer.setAttribute('aria-hidden', 'true');
  layer.style.cssText = 'position:fixed;inset:0;z-index:200;overflow:hidden;pointer-events:none;background-color:rgba(0,0,0,0)';
  layer.append(clone);
  document.body.append(layer);
  source.style.visibility = 'hidden';

  // The theme's own page transition would fade and slide the page over this handoff.
  window.addEventListener(
    'pageswap',
    (event) => {
      const transition = event.viewTransition;
      if (!transition) return;
      transition.skipTransition();
      [transition.ready, transition.finished, transition.updateCallbackDone].forEach((promise) => promise?.catch(() => {}));
    },
    { once: true }
  );

  const scale = Math.min((window.innerWidth * 0.9) / rect.width, (window.innerHeight * 0.86) / rect.height);
  const x = window.innerWidth / 2 - (rect.left + rect.width / 2);
  const y = window.innerHeight / 2 - (rect.top + rect.height / 2);

  const handoff = () => {
    try {
      sessionStorage.setItem(
        'melonary:zoom-handoff',
        JSON.stringify({
          ts: Date.now(),
          path: new URL(link.href, window.location.origin).pathname,
          bg: BONE,
          x,
          y,
          scale,
          html: clone.outerHTML.replace(/transform:[^;"]*;?/g, ''),
        })
      );
    } catch {
      // Storage can be unavailable (private mode); the product page just opens without the handoff.
    }
    window.location.href = link.href;
  };

  gsap
    .timeline({ onComplete: handoff })
    .to(layer, { backgroundColor: BONE, duration: 0.45, ease: 'power2.out' }, 0)
    .to(clone, { x, y, scale, duration: 0.9, ease: 'expo.inOut' }, 0);
  return true;
}

function clear() {
  layer?.remove();
  layer = null;
  busy = false;
  document.querySelectorAll(SOURCE).forEach((el) => (el.style.visibility = ''));
}

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('a[data-zoom-product]');
  if (!link || event.defaultPrevented || event.button !== 0 || busy) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const source = link.querySelector(SOURCE);
  if (!source?.getBoundingClientRect().width) return;

  event.preventDefault();
  busy = true;
  fly(link, source)
    .then((started) => {
      if (!started) window.location.href = link.href;
    })
    .catch(() => {
      clear();
      window.location.href = link.href;
    });
});

// Coming back with the browser's back button restores the page mid-flight; put it back as it was.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) clear();
});
